import { Controller } from "@hotwired/stimulus"
import { debounce } from "lodash"

export default class extends Controller {
  static targets = ["categoryResults", "tagResults", "activeFilters"]

  connect() {
    this.debouncedCategorySearch = debounce(this.loadCategories.bind(this), 300)
    this.debouncedTagSearch = debounce(this.loadTags.bind(this), 300)
    
    // Load initial data if needed
    this.loadCategories('')
    this.loadTags('')
  }

  searchCategories(event) {
    const query = event.target.value.trim()
    this.debouncedCategorySearch(query)
  }

  searchTags(event) {
    const query = event.target.value.trim()
    this.debouncedTagSearch(query)
  }

  loadCategories(query) {
    fetch(`/search/categories?q=${encodeURIComponent(query)}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.displayCategories(data)
    })
    .catch(error => {
      console.error('Category search error:', error)
    })
  }

  loadTags(query) {
    fetch(`/search/tags?q=${encodeURIComponent(query)}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json())
    .then(data => {
      this.displayTags(data)
    })
    .catch(error => {
      console.error('Tag search error:', error)
    })
  }

  displayCategories(categories) {
    if (!categories || categories.length === 0) {
      this.categoryResultsTarget.innerHTML = `
        <div class="text-center text-slate-400 py-2">
          No categories found
        </div>
      `
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const selectedCategoryId = urlParams.get('category')
    
    const categoriesHtml = categories.map(category => {
      const isSelected = selectedCategoryId && Number(selectedCategoryId) === Number(category.id)
      const bgColor = isSelected ? 'bg-blue-600' : 'hover:bg-slate-700'
      const textColor = isSelected ? 'text-white' : 'text-white'
      
      return `
        <div class="px-2 py-1 ${bgColor} rounded cursor-pointer flex items-center justify-between"
             data-action="click->search-filters#selectCategory"
             data-category-id="${category.id}"
             data-category-name="${category.name}">
          <span class="text-sm ${textColor}">${category.name}</span>
          <span class="text-xs ${isSelected ? 'text-blue-200' : 'text-slate-400'}">${category.products_count || 0} products</span>
        </div>
      `
    }).join('')
    
    this.categoryResultsTarget.innerHTML = categoriesHtml
  }

  displayTags(tags) {
    if (!tags || tags.length === 0) {
      this.tagResultsTarget.innerHTML = `
        <div class="text-center text-slate-400 py-2">
          No tags found
        </div>
      `
      return
    }

    const urlParams = new URLSearchParams(window.location.search)
    const selectedTagIds = urlParams.get('tags') ? urlParams.get('tags').split(',').map(id => parseInt(id)) : []
    
    const tagsHtml = tags.map(tag => {
      const isSelected = selectedTagIds.includes(Number(tag.id))
      const icon = isSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
      
      return `
        <div class="px-2 py-1 hover:bg-slate-700 rounded cursor-pointer flex items-center justify-between"
             data-action="click->search-filters#toggleTagSelection"
             data-tag-id="${tag.id}"
             data-tag-name="${tag.name}">
          <span class="text-sm text-white">${tag.name}</span>
          <span class="flex items-center gap-1">
            <span class="text-xs text-slate-400 mr-2">${tag.products_count || 0} products</span>
            <iconify-icon class="tag-selection-indicator" icon="${icon}"></iconify-icon>
          </span>
        </div>
      `
    }).join('')
    
    this.tagResultsTarget.innerHTML = tagsHtml
  }

  selectCategory(event) {
    const categoryId = event.currentTarget.dataset.categoryId
    const categoryName = event.currentTarget.dataset.categoryName
    
    // Get current search parameters
    const urlParams = new URLSearchParams(window.location.search)
    const keyword = urlParams.get('q') || ''
    const tagIds = urlParams.get('tags') || ''
    
    // Build new URL with category filter
    let newParams = new URLSearchParams()
    newParams.set('q', keyword)
    newParams.set('category', categoryId)
    if (tagIds) newParams.set('tags', tagIds)
    
    window.location.href = `/search?${newParams.toString()}`
  }

  toggleTagSelection(event) {
    const tagId = event.currentTarget.dataset.tagId
    const tagName = event.currentTarget.dataset.tagName
    
    // Get current search parameters
    const urlParams = new URLSearchParams(window.location.search)
    const keyword = urlParams.get('q') || ''
    const categoryId = urlParams.get('category') || ''
    let selectedTagIds = urlParams.get('tags') ? urlParams.get('tags').split(',').map(id => parseInt(id)) : []
    
    // Check if tag is already selected
    const existingIndex = selectedTagIds.findIndex(id => id === Number(tagId))
    
    if (existingIndex >= 0) {
      // Remove tag if already selected
      selectedTagIds.splice(existingIndex, 1)
      // Update visual indicator
      const indicator = event.currentTarget.querySelector('.tag-selection-indicator')
      if (indicator) {
        indicator.setAttribute('icon', 'mdi-checkbox-blank-outline')
      }
    } else {
      // Add tag if not selected
      selectedTagIds.push(Number(tagId))
      // Update visual indicator
      const indicator = event.currentTarget.querySelector('.tag-selection-indicator')
      if (indicator) {
        indicator.setAttribute('icon', 'mdi-checkbox-marked')
      }
    }
    
    // Build new URL with updated tags
    let newParams = new URLSearchParams()
    newParams.set('q', keyword)
    if (categoryId) newParams.set('category', categoryId)
    if (selectedTagIds.length > 0) newParams.set('tags', selectedTagIds.join(','))
    
    window.location.href = `/search?${newParams.toString()}`
  }
}